import React, { useState, useEffect, useMemo, useContext } from "react";

import DataRangeDisplay from "@/components/DataRangeDisplay";
import JoinedCampaignCard from "./JoinedCampaignCard";
import NoDataFoundBase from "@/components/NoDataFound";
import NotAvailable from "@/components/NotAvailable";
import TablePagination from "@/components/tables/TablePagination";
import { CAMPAIGN_CONST, USER_ROLE } from "@/config/constants";
import { GlobalContext } from "@/helpers/contexts/contexts";

const NoDataFound = NoDataFoundBase as React.ComponentType<any>;

type JoinedCampaign = {
  id?: string | number;
  status?: string | number;
  is_campaign_boost?: boolean;
  can_participant_extend_campaign?: boolean;
  pause_campaign_available?: boolean;
  [key: string]: any;
};

type JoinedCampaignsProps = {
  joinedCampaigns?: JoinedCampaign[];
  pageCount: number;
  pageIndex: number;
  onPageChange?: (_event: React.ChangeEvent<unknown>, _page: number) => void;
  recordsTotal: number;
  isLoading?: boolean;
};

export default function JoinedCampaigns({
  joinedCampaigns = [],
  pageCount,
  pageIndex,
  onPageChange,
  recordsTotal,
  isLoading,
}: JoinedCampaignsProps) {
  const { selectedBrand, loggedInUserRole } = useContext(GlobalContext);
  const [campaignData, setCampaignData] = useState<JoinedCampaign[]>([]);

  useEffect(() => {
    setCampaignData(joinedCampaigns);
  }, [joinedCampaigns]);

  const renderPagination = () => {
    return (
      <div className="row">
        <div className="col-lg-6 mb-2 d-flex align-items-center">
          <DataRangeDisplay totalRecords={recordsTotal} pageIndex={pageIndex} />
        </div>
        <div className="col-6">
          <TablePagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={onPageChange}
            className="float-lg-right float-none"
          />
        </div>
      </div>
    );
  };

  const isHideBudget = useMemo(() => {
    return (
      selectedBrand?.id &&
      CAMPAIGN_CONST.HIDE_CAMPAIGN_BUDGET.includes(selectedBrand?.id)
    );
  }, [selectedBrand?.id]);

  const hasAnyActionInList = useMemo(() => {
    if (!campaignData.length || !selectedBrand) return false;

    return campaignData.some((campaign) => {
      const hasBoost =
        !!campaign?.is_campaign_boost &&
        selectedBrand?.status &&
        (campaign?.status === CAMPAIGN_CONST.STATUS.ACTIVE.ID ||
          campaign?.status === CAMPAIGN_CONST.STATUS.SCHEDULED.ID);

      const hasExtend =
        campaign?.can_participant_extend_campaign &&
        campaign?.status === CAMPAIGN_CONST.STATUS.ACTIVE.ID;

      const hasPause =
        campaign && selectedBrand?.status && campaign.pause_campaign_available;

      return hasBoost || hasExtend || hasPause;
    });
  }, [campaignData, selectedBrand]);

  if (isLoading) {
    return <NoDataFound className="my-5" message="Loading..." />;
  }

  return (
    <>
      <div className="joined-campaigns mb-1 pt-3">
        {campaignData.length ? (
          <>
            <div className="row align-items-stretch">
              <div className="col-12 d-none d-lg-block px-2">
                <div className="card border-0 heading-card">
                  <div className="card-body py-4 px-3">
                    <div className="row">
                      <div className="col-lg-2 title">Campaign Name</div>
                      <div className="col-lg title pl-lg-1">Status</div>
                      {hasAnyActionInList && (
                        <div className="col-lg-2 title text-left p-0">
                          Actions
                        </div>
                      )}
                      {!isHideBudget && (
                        <div
                          className={`col-lg-1 title ${hasAnyActionInList ? "col-lg-1" : "col-lg-2"}`}
                        >
                          Budget
                        </div>
                      )}
                      <div
                        className={`col-lg-1 title ${hasAnyActionInList ? "col-lg-1" : "col-lg-2"}`}
                      >
                        Enrolled Locations
                      </div>
                      {loggedInUserRole === USER_ROLE.PARTICIPANT.NAME ? (
                        <div className="col-lg title">Dates</div>
                      ) : null}
                      {loggedInUserRole ===
                      USER_ROLE.PARTICIPANT.NAME ? null : (
                        <div className="col-lg title">Start Date</div>
                      )}
                      {loggedInUserRole ===
                      USER_ROLE.PARTICIPANT.NAME ? null : (
                        <div className="col-lg title">End Date</div>
                      )}
                      <div className="col-lg title">Channels</div>
                      <div className="col-lg-1 title">FAQ</div>
                    </div>
                  </div>
                </div>
              </div>
              {campaignData.map((c) => (
                <div
                  className="col-lg-12 col-md-6 mb-1 mb-lg-auto p-2 py-lg-0"
                  key={c.id}
                >
                  <JoinedCampaignCard
                    campaign={c}
                    isHideBudget={isHideBudget}
                    showActionsColumn={hasAnyActionInList}
                  />
                </div>
              ))}
            </div>
            {renderPagination()}
          </>
        ) : (
          <NotAvailable message="No campaign reports are currently available." />
        )}
      </div>
    </>
  );
}
